import { delay, put, putResolve, select, spawn, take, takeEvery, takeLatest } from 'typed-redux-saga/macro';
import { getPots } from 'features/pots/actions';
import { selectIsGBUser } from 'reducers/selectors';
import { selectPendingTopupsFlag } from '../reducers/selectors';
import { fetchTradingAccountInfoAfterOrder, fetchWealthAccounts, getActivities, getOrders, getPositions, updateWealthAccounts, } from '../actions';
import { getPendingTopups, getPendingTopupsForAccountId, } from '../actions/topup';
import { SellOrderType } from '../types';
/**
 * Just check for pending topups for 2 minutes, with 10 seconds delay.
 */
function* pollForPendingTopups() {
    for (let index = 0; index < 12; index += 1) {
        yield* putResolve(getPendingTopups());
        yield* putResolve(getPots());
        yield* delay(10000);
    }
}
function* pollForGiaPendingTopupsWorker(action) {
    const { accountId } = action;
    for (let index = 0; index < 5; index += 1) {
        yield* putResolve(getPendingTopupsForAccountId(accountId));
        yield* delay(1000);
    }
}
function* updateBalancesWhenPendingTopupsGoAway() {
    let pendingTopupPaymentsExist = yield* select(selectPendingTopupsFlag);
    while (true) {
        const successAction = yield* take((action) => action.type === 'GET_PENDING_TOPUPS_SUCCESS');
        const pendingTopupPaymentsExistOnServer = successAction.payload.pendingTopUps.length > 0;
        if (pendingTopupPaymentsExist && !pendingTopupPaymentsExistOnServer) {
            yield* put(updateWealthAccounts());
        }
        pendingTopupPaymentsExist = pendingTopupPaymentsExistOnServer;
    }
}
// This generator fetches wealth accounts n times expecting change in balance
function* fetchWealthAccountsWorker(action) {
    const isGBUser = yield* select(selectIsGBUser);
    if (isGBUser && action.retryTimes) {
        yield* delay(1000);
        yield* putResolve(updateWealthAccounts({ skipGetPositions: true }));
        yield* putResolve(getActivities({ accountId: action.tradingAccountId, paging: { page: 1, perPage: 30 } }));
        yield* put(fetchWealthAccounts(action.retryTimes - 1, action.tradingAccountId));
    }
}
function* pollForInformationAfterOrderCreation(action) {
    const isGBUser = yield* select(selectIsGBUser);
    if (isGBUser) {
        const { retryTimes, tradingAccountId, assetId } = action;
        yield* putResolve(getActivities({ accountId: tradingAccountId, paging: { page: 1, perPage: 30 } }));
        yield* putResolve(getActivities({
            accountId: tradingAccountId,
            assetIds: [assetId],
            paging: { page: 1, perPage: 30 },
        }));
        yield* putResolve(getPositions({ accountId: tradingAccountId, paging: { page: 1, perPage: 10 ** 6 } }));
        // Refreshing market orders for symbol as well as the main list
        yield* putResolve(getOrders({
            accountId: tradingAccountId,
            statuses: ['queued', 'pending'],
            orderTypes: [SellOrderType.MARKET],
        }));
        yield* putResolve(getOrders({
            assetIds: [assetId],
            accountId: tradingAccountId,
            statuses: ['queued', 'pending'],
            orderTypes: [SellOrderType.MARKET],
        }));
        if (action.retryTimes) {
            yield* delay(1000);
            yield* putResolve(fetchTradingAccountInfoAfterOrder(retryTimes - 1, tradingAccountId, assetId));
        }
    }
}
function* topupSaga() {
    yield* takeLatest('POLL_PENDING_TOPUPS_FOR_TRADING_ACCOUNT_ID', pollForGiaPendingTopupsWorker);
    yield* takeLatest('FETCH_WEALTH_ACCOUNTS', fetchWealthAccountsWorker);
    yield* takeLatest('FETCH_TRADING_ACCOUNT_INFO_AFTER_ORDER', pollForInformationAfterOrderCreation);
    yield* takeEvery((action) => action.type === 'TOPUP_CREATED', pollForPendingTopups); // after user tops up, we start polling for pending topups
    yield* spawn(updateBalancesWhenPendingTopupsGoAway);
}
export default topupSaga;
